/**
 * Affiliates Panel — Painel do afiliado (customer-facing)
 * Dashboard, Campanhas Disponíveis, Minhas Campanhas, Meus Ganhos
 */

import { T } from '../styles/tokens';
import { useState, useEffect } from 'preact/hooks';
import { api } from '../api/client';
import { SkeletonBar, Textarea, Button, Input, Select, TabBar, Modal, MetricCard, Badge as UIBadge } from '../components/ui';

const s = {
  page: { padding: '24px 32px', maxWidth: 1200, margin: '0 auto' } as any,
  title: { fontSize: 24, fontWeight: 700, color: T.text, marginBottom: 8 } as any,
  subtitle: { fontSize: 14, color: T.textMuted, marginBottom: 24 } as any,
  card: { background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 12, padding: 20 } as any,
  tabBar: { display: 'flex', gap: 0, borderBottom: `1px solid ${T.border}`, marginBottom: 24, overflowX: 'auto' as any } as any,
  tab: (active: boolean) => ({
    padding: '10px 16px', cursor: 'pointer', fontSize: 13, fontWeight: active ? 600 : 500,
    color: active ? T.primary : T.textSecondary, borderBottom: active ? '2px solid #3B82F6' : '2px solid transparent',
    whiteSpace: 'nowrap' as any, transition: 'all 0.15s',
  }),
  input: { width: '100%', padding: '8px 12px', borderRadius: 8, fontSize: 14, background: T.bgCardHover, border: `1px solid ${T.borderHover}`, color: T.text, outline: 'none', boxSizing: 'border-box' as any } as any,
  select: { width: '100%', padding: '8px 12px', borderRadius: 8, fontSize: 14, background: T.bgCardHover, border: `1px solid ${T.borderHover}`, color: T.text, outline: 'none', boxSizing: 'border-box' as any } as any,
  textarea: { width: '100%', padding: '8px 12px', borderRadius: 8, fontSize: 13, background: T.bgCardHover, border: `1px solid ${T.borderHover}`, color: T.text, outline: 'none', minHeight: 80, resize: 'vertical' as any, boxSizing: 'border-box' as any } as any,
  btn: (variant: 'primary' | 'danger' | 'ghost' | 'success') => ({
    padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none',
    display: 'inline-flex', alignItems: 'center', gap: 6, transition: 'all 0.15s',
    ...(variant === 'primary' ? { background: T.primary, color: T.text } :
       variant === 'danger' ? { background: T.danger, color: T.text } :
       variant === 'success' ? { background: T.success, color: T.text } :
       { background: T.border, color: T.textSecondary }),
  }),
  table: { width: '100%', borderCollapse: 'collapse' as any, fontSize: 13 } as any,
  th: { textAlign: 'left' as any, padding: '10px 12px', color: T.textSecondary, fontWeight: 600, fontSize: 11, textTransform: 'uppercase' as any, borderBottom: `1px solid ${T.border}` } as any,
  td: { padding: '12px 12px', color: T.text, borderBottom: `1px solid ${T.borderSubtle}` } as any,
  badge: (color: string) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600,
    background: `${color}20`, color: color,
  }),
  label: { fontSize: 12, fontWeight: 600, color: T.textSecondary, marginBottom: 4, display: 'block' } as any,
  formGroup: { marginBottom: 16 } as any,
  metricCard: { background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 12, padding: '16px 20px', textAlign: 'center' as any } as any,
  overlay: { position: 'fixed' as any, inset: 0, background: T.bgOverlay, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modal: { background: T.bg, border: `1px solid ${T.borderHover}`, borderRadius: 16, padding: 24, width: 460, maxWidth: '90vw' } as any,
};

const TYPE_LABELS: Record<string, string> = { trial: 'Trial', credits: 'Créditos', discount_percent: 'Desconto %' };
const CHANNEL_OPTIONS = ['Blog', 'YouTube', 'Instagram', 'TikTok', 'Twitter/X', 'Email Marketing', 'Comunidades', 'Outro'];

// ── Theme tokens (matches CreateAgentModal) ──
const M = {
  bg: T.bg,
  card: T.bgInput,
  primary: T.primary,
  text: T.text,
  muted: T.textSecondary,
  dimmed: T.textMuted,
  success: T.success,
  warning: T.warning,
  error: T.danger,
  borderSolid: T.border,
  font: "'Manrope', system-ui, sans-serif",
  mono: "'JetBrains Mono', 'Fira Code', monospace",
};

const modalInputStyle: any = {
  width: '100%', padding: '10px 14px', borderRadius: 10, border: `1px solid ${M.borderSolid}`,
  background: T.bgCard, color: M.text, fontSize: 13,
  fontFamily: M.font, outline: 'none', boxSizing: 'border-box',
};

const modalSelectStyle: any = { ...modalInputStyle, cursor: 'pointer', appearance: 'auto' };

const modalTextareaStyle: any = {
  ...modalInputStyle, minHeight: 80, resize: 'vertical' as any, fontFamily: M.font,
};

function ModalFieldGroup({ label, desc, children }: { label: string; desc?: string; children: any }) {
  return (
    <div style={{ flexShrink: 0 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: M.text }}>{label}</span>
        {desc && <span style={{ fontSize: 11, color: M.dimmed }}>{desc}</span>}
      </div>
      {children}
    </div>
  );
}

// ── Inscrição de afiliado ──
function AffiliateApplySection() {
  const [affiliate, setAffiliate] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ full_name: '', promotion_channels: '', promotion_strategy: '', application_notes: '', agreed: false });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/affiliates/me');
        const data = await res.json() as any;
        if (data.success) setAffiliate(data.data.affiliate);
      } catch {} finally { setLoading(false); }
    })();
  }, []);

  const submit = async () => {
    if (!form.agreed || !form.full_name || !form.promotion_strategy) return;
    setError('');
    try {
      setSubmitting(true);
      const res = await api.post('/affiliates/apply', {
        full_name: form.full_name,
        promotion_channels: form.promotion_channels,
        promotion_strategy: form.promotion_strategy,
        application_notes: form.application_notes,
      });
      const data = await res.json() as any;
      if (data.success) {
        setAffiliate(data.data.affiliate);
        setShowModal(false);
      } else {
        setError(data.error || 'Erro ao enviar solicitação');
      }
    } catch {
      setError('Erro de conexão. Tente novamente.');
    } finally { setSubmitting(false); }
  };

  if (loading) return <div style={{ padding: 16 }}><SkeletonBar width="50%" height={14} style={{ marginBottom: 8 }} /><SkeletonBar width="30%" height={14} /></div>;

  // Se já é afiliado aprovado
  if (affiliate?.status === 'approved') {
    return (
      <div style={{ ...s.card, textAlign: 'center', padding: 48 }}>
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={M.success} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 16 }}>
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
        </svg>
        <div style={{ fontSize: 18, fontWeight: 700, color: M.success, marginBottom: 8, fontFamily: M.font }}>Conta de afiliado ativa</div>
        <div style={{ fontSize: 13, color: M.muted, marginBottom: 24 }}>Acesse seu painel para gerenciar campanhas e acompanhar ganhos.</div>
        <a href="/affiliates" style={{ ...s.btn('primary'), textDecoration: 'none', fontFamily: M.font }}>Acessar Painel</a>
      </div>
    );
  }

  if (affiliate?.status === 'pending') {
    return (
      <div style={{ ...s.card, textAlign: 'center', padding: 48 }}>
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={M.warning} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 16 }}>
          <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
        </svg>
        <div style={{ fontSize: 18, fontWeight: 700, color: M.warning, marginBottom: 8, fontFamily: M.font }}>Solicitação em análise</div>
        <div style={{ fontSize: 13, color: M.muted }}>Aguarde a aprovação da equipe. Você será notificado quando for aprovado.</div>
      </div>
    );
  }

  if (affiliate?.status === 'rejected') {
    return (
      <div style={{ ...s.card, textAlign: 'center', padding: 48 }}>
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={M.error} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 16 }}>
          <circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
        </svg>
        <div style={{ fontSize: 18, fontWeight: 700, color: M.error, marginBottom: 8, fontFamily: M.font }}>Solicitação negada</div>
        {affiliate.rejection_reason && <div style={{ fontSize: 13, color: M.muted, marginBottom: 16 }}>Motivo: {affiliate.rejection_reason}</div>}
        <Button variant="primary" onClick={() => { setAffiliate(null); setShowModal(true); }}>Tentar novamente</Button>
      </div>
    );
  }

  return (
    <>
      <div style={{ ...s.card, textAlign: 'center', padding: 48 }}>
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={M.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 16 }}>
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
        <div style={{ fontSize: 18, fontWeight: 700, color: M.text, marginBottom: 8, fontFamily: M.font }}>Programa de Afiliados</div>
        <div style={{ fontSize: 13, color: M.muted, marginBottom: 24, maxWidth: 400, margin: '0 auto 24px' }}>
          Ganhe comissões indicando novos clientes. Crie campanhas, acompanhe conversões e receba seus ganhos em tempo real.
        </div>
        <Button variant="primary" onClick={() => setShowModal(true)}>Solicitar acesso</Button>
      </div>

      {showModal && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 9999999,
            background: T.bgOverlay, backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
            animation: 'affApplyOverlayIn 0.2s ease',
          }}
          onClick={() => { if (!submitting) setShowModal(false); }}
        >
          <div
            className="aff-apply-modal-inner"
            onClick={(e: any) => e.stopPropagation()}
            style={{
              background: T.bgOverlay, backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              borderRadius: 20, border: `1px solid ${M.borderSolid}`,
              width: '100%', maxWidth: 540, maxHeight: '85vh',
              display: 'flex', flexDirection: 'column',
              boxShadow: `0 25px 60px ${T.blackAlpha50}`,
              animation: 'affApplyIn 0.25s ease',
              fontFamily: M.font, overflow: 'hidden',
            }}
          >
            {/* Header */}
            <div style={{
              padding: '24px 28px 16px',
              borderBottom: `1px solid ${M.borderSolid}`,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 19, fontWeight: 800, color: M.text, fontFamily: M.font }}>
                  Solicitar Acesso
                </h3>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: M.muted }}>
                  Programa de Afiliados WhiteLabelClaw
                </p>
              </div>
              <Button variant="ghost" size="xs" onClick={() => { if (!submitting) setShowModal(false); }} disabled={submitting}>&#x2715;</Button>
            </div>

            {/* Scrollable content */}
            <div style={{
              padding: '20px 28px 28px',
              display: 'flex', flexDirection: 'column', gap: 16,
              overflowY: 'auto', overflowX: 'hidden', flexGrow: 1, minHeight: 0,
            }}>

              {/* Error banner */}
              {error && (
                <div style={{
                  padding: '10px 14px', borderRadius: 10,
                  background: T.dangerBg, border: `1px solid ${T.dangerBgStrong}`,
                  fontSize: 12, color: T.danger,
                }}>{error}</div>
              )}

              <ModalFieldGroup label="Nome Completo" desc="obrigatorio">
                <Input
                  placeholder="Seu nome completo"
                  value={form.full_name}
                  onChange={(v) => setForm(p => ({ ...p, full_name: v }))}
                />
              </ModalFieldGroup>

              <ModalFieldGroup label="Canal de divulgação" desc="como pretende promover">
                <Select
                  value={form.promotion_channels}
                  onChange={(v) => setForm(p => ({ ...p, promotion_channels: v }))}
                  placeholder="Selecione..."
                  options={CHANNEL_OPTIONS.map(ch => ({ value: ch, label: ch }))}
                />
              </ModalFieldGroup>

              <ModalFieldGroup label="Estratégia de promoção" desc="obrigatório">
                <Textarea
                  value={form.promotion_strategy}
                  onInput={(e) => setForm(p => ({ ...p, promotion_strategy: (e.target as HTMLTextAreaElement).value }))}
                  placeholder="Descreva como pretende divulgar WhiteLabelClaw"
                  style={modalTextareaStyle}
                />
              </ModalFieldGroup>

              <ModalFieldGroup label="Observações" desc="opcional">
                <Textarea
                  value={form.application_notes}
                  onInput={(e) => setForm(p => ({ ...p, application_notes: (e.target as HTMLTextAreaElement).value }))}
                  placeholder="Alguma informação adicional..."
                  style={{ ...modalTextareaStyle, minHeight: 50 }}
                />
              </ModalFieldGroup>

              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={form.agreed}
                  onChange={(e) => setForm(p => ({ ...p, agreed: (e.target as HTMLInputElement).checked }))}
                  style={{ accentColor: M.primary, marginTop: 2, flexShrink: 0 }}
                />
                <span style={{ fontSize: 12, color: M.muted, lineHeight: 1.5 }}>
                  Li e aceito os termos do programa de afiliados
                </span>
              </label>
            </div>

            {/* Footer */}
            <div style={{
              padding: '16px 28px',
              borderTop: `1px solid ${M.borderSolid}`,
              display: 'flex', gap: 12, justifyContent: 'flex-end', flexShrink: 0,
            }}>
              <Button variant="ghost" onClick={() => { if (!submitting) setShowModal(false); }} disabled={submitting}>
                Cancelar
              </Button>
              <Button
                variant="primary"
                disabled={!form.agreed || !form.full_name || !form.promotion_strategy || submitting}
                loading={submitting}
                onClick={submit}
              >
                {submitting ? 'Enviando...' : 'Enviar Solicitação'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes affApplyOverlayIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes affApplyIn {
          from { opacity: 0; transform: scale(0.92) translateY(10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes affApplySpin {
          to { transform: rotate(360deg); }
        }
        @media (max-width: 640px) {
          .aff-apply-modal-inner {
            border-radius: 0 !important;
            max-height: 100vh !important;
            height: 100vh !important;
            max-width: 100% !important;
          }
        }
      `}</style>
    </>
  );
}

// ── Painel principal do afiliado ──
const TABS = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'campaigns', label: '📢 Campanhas Disponíveis' },
  { id: 'mine', label: 'Minhas Campanhas' },
  { id: 'earnings', label: 'Meus Ganhos' },
];

export function AffiliatesPanel() {
  const [affiliate, setAffiliate] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState<any>(null);
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [myCampaigns, setMyCampaigns] = useState<any[]>([]);
  const [earnings, setEarnings] = useState<any[]>([]);
  const [settings, setSettings] = useState<any>(null);
  const [affiliateId, setAffiliateId] = useState('');
  const [copied, setCopied] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', type: 'credits', value: 0, trial_days: 0, plan: 'pro', max_redemptions: 50 });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/affiliates/me');
        const data = await res.json() as any;
        if (data.success) setAffiliate(data.data.affiliate);
      } catch {} finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => {
    if (!affiliate || affiliate.status !== 'approved') return;
    if (tab === 'dashboard') fetchDashboard();
    if (tab === 'campaigns') fetchCampaigns();
    if (tab === 'mine') fetchMyCampaigns();
    if (tab === 'earnings') fetchEarnings();
  }, [tab, affiliate]);

  const fetchDashboard = async () => {
    try {
      const res = await api.get('/affiliates/dashboard');
      const data = await res.json() as any;
      if (data.success) setDashboard(data.data);
    } catch {}
  };

  const fetchCampaigns = async () => {
    try {
      const res = await api.get('/affiliates/campaigns');
      const data = await res.json() as any;
      if (data.success) {
        setCampaigns(data.data.campaigns || []);
        setSettings(data.data.settings);
        setAffiliateId(data.data.affiliate_id || '');
      }
    } catch {}
  };

  const fetchMyCampaigns = async () => {
    try {
      const res = await api.get('/affiliates/campaigns/mine');
      const data = await res.json() as any;
      if (data.success) setMyCampaigns(data.data.campaigns || []);
    } catch {}
  };

  const fetchEarnings = async () => {
    try {
      const res = await api.get('/affiliates/earnings');
      const data = await res.json() as any;
      if (data.success) setEarnings(data.data.earnings || []);
    } catch {}
  };

  const copyLink = (code: string) => {
    const link = `' + window.location.origin + '?promo=${code}&ref=${affiliateId}&utm_source=affiliate&utm_medium=referral&utm_campaign=${affiliateId}`;
    navigator.clipboard.writeText(link);
    setCopied(code);
    setTimeout(() => setCopied(''), 2000);
  };

  const createCampaign = async () => {
    try {
      setCreating(true);
      await api.post('/affiliates/campaigns', createForm);
      setShowCreate(false);
      fetchMyCampaigns();
    } catch {} finally { setCreating(false); }
  };

  const fmt = (d: string) => d ? new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '-';

  if (loading) return <div style={{ padding: 16 }}><SkeletonBar width="50%" height={14} style={{ marginBottom: 8 }} /><SkeletonBar width="30%" height={14} /></div>;

  // Se não é afiliado aprovado, mostrar seção de inscrição
  if (!affiliate || affiliate.status !== 'approved') {
    return (
      <div style={s.page}>
        <h1 style={s.title}>Programa de Afiliados</h1>
        <div style={s.subtitle}>Ganhe comissões indicando WhiteLabelClaw</div>
        <AffiliateApplySection />
      </div>
    );
  }

  return (
    <div style={s.page}>
      <h1 style={s.title}>Painel de Afiliado</h1>
      <div style={s.subtitle}>Bem-vindo, {affiliate.full_name}! Gerencie suas campanhas e acompanhe seus ganhos.</div>

      <TabBar tabs={TABS} active={tab} onChange={setTab} />

      {/* ── Dashboard ── */}
      {tab === 'dashboard' && dashboard && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 24 }}>
            <MetricCard value={dashboard.total_referrals || 0} label="Total Referrals" color={T.primary} />
            <MetricCard value={`$${dashboard.llm_month?.toFixed(2) || '0.00'}`} label="Ganhos LLM (mês)" color={T.success} />
            <MetricCard value={`$${dashboard.sub_month?.toFixed(2) || '0.00'}`} label="Ganhos Sub (mês)" color={T.purple} />
            <MetricCard value={`$${dashboard.total_earnings?.toFixed(2) || '0.00'}`} label="Saldo Total" color={T.warning} />
          </div>

          {/* Gráfico simples */}
          {dashboard.monthly_chart?.length > 0 && (
            <div style={s.card}>
              <div style={{ fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 16 }}>Ganhos por Mês</div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 120 }}>
                {dashboard.monthly_chart.slice().reverse().map((m: any) => {
                  const maxVal = Math.max(...dashboard.monthly_chart.map((x: any) => x.total || 1));
                  const height = Math.max(((m.total || 0) / maxVal) * 100, 4);
                  return (
                    <div key={m.month} style={{ flex: 1, textAlign: 'center' }}>
                      <div style={{ background: T.primary, borderRadius: '4px 4px 0 0', height: `${height}px`, margin: '0 auto', maxWidth: 40 }} />
                      <div style={{ fontSize: 10, color: T.textMuted, marginTop: 4 }}>{m.month}</div>
                      <div style={{ fontSize: 10, color: T.textSecondary }}>${m.total?.toFixed(0)}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Campanhas Disponíveis ── */}
      {tab === 'campaigns' && (
        <div>
          {campaigns.length === 0 ? (
            <div style={{ ...s.card, textAlign: 'center', color: T.textMuted }}>Nenhuma campanha disponível no momento</div>
          ) : (
            <div style={{ display: 'grid', gap: 12 }}>
              {campaigns.map((c: any) => (
                <div key={c.id} style={{ ...s.card, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{c.name || c.code}</div>
                    <div style={{ fontSize: 12, color: T.textSecondary, marginTop: 4 }}>
                      <UIBadge variant="info">{TYPE_LABELS[c.type] || c.type}</UIBadge>
                      {c.type === 'credits' && <span style={{ marginLeft: 8 }}>${c.value}</span>}
                      {c.type === 'trial' && <span style={{ marginLeft: 8 }}>{c.trial_days} dias</span>}
                      {c.type === 'discount_percent' && <span style={{ marginLeft: 8 }}>{c.value}%</span>}
                      <span style={{ marginLeft: 12, color: T.textMuted }}>
                        {c.total_redemptions || 0}/{c.max_redemptions || '∞'} resgates
                      </span>
                    </div>
                  </div>
                  <Button variant={copied === c.code ? 'success' : 'primary'} size="sm" onClick={() => copyLink(c.code)}>
                    {copied === c.code ? 'Copiado!' : 'Copiar meu link'}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Minhas Campanhas ── */}
      {tab === 'mine' && (
        <div>
          <div style={{ marginBottom: 16 }}>
            <Button variant="primary" size="sm" onClick={() => setShowCreate(true)}>➕ Criar Campanha</Button>
          </div>
          {myCampaigns.length === 0 ? (
            <div style={{ ...s.card, textAlign: 'center', color: T.textMuted }}>Você ainda não criou nenhuma campanha</div>
          ) : (
            <table style={s.table}>
              <thead><tr>
                <th style={s.th}>Código</th><th style={s.th}>Nome</th><th style={s.th}>Tipo</th>
                <th style={s.th}>Valor</th><th style={s.th}>Resgates</th><th style={s.th}>Status</th><th style={s.th}>Link</th>
              </tr></thead>
              <tbody>
                {myCampaigns.map((c: any) => (
                  <tr key={c.id}>
                    <td style={s.td}><code style={{ fontSize: 11 }}>{c.code}</code></td>
                    <td style={s.td}>{c.name || '-'}</td>
                    <td style={s.td}><UIBadge variant="info">{TYPE_LABELS[c.type] || c.type}</UIBadge></td>
                    <td style={s.td}>{c.type === 'credits' ? `$${c.value}` : c.type === 'trial' ? `${c.trial_days}d` : `${c.value}%`}</td>
                    <td style={s.td}>{c.total_redemptions || 0}/{c.max_redemptions || '∞'}</td>
                    <td style={s.td}><UIBadge variant={c.status === 'active' ? 'success' : 'neutral'}>{c.status}</UIBadge></td>
                    <td style={s.td}>
                      <Button variant={copied === c.code ? 'success' : 'ghost'} size="xs" onClick={() => copyLink(c.code)}>
                        {copied === c.code ? '' : ''}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Modal criar campanha */}
          <Modal open={showCreate} onClose={() => setShowCreate(false)} title="➕ Criar Campanha"
            footer={
              <>
                <Button variant="ghost" onClick={() => setShowCreate(false)}>Cancelar</Button>
                <Button variant="primary" disabled={creating} loading={creating} onClick={createCampaign}>
                  {creating ? 'Criando...' : 'Criar'}
                </Button>
              </>
            }
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <Input label="Nome da Campanha" value={createForm.name} onChange={(v) => setCreateForm(p => ({ ...p, name: v }))} placeholder="Ex: Promoção YouTube" />
              <Select
                label="Tipo"
                value={createForm.type}
                onChange={(v) => setCreateForm(p => ({ ...p, type: v }))}
                options={[
                  { value: 'credits', label: 'Créditos' },
                  { value: 'trial', label: 'Trial' },
                  { value: 'discount_percent', label: 'Desconto %' },
                ]}
              />
              {createForm.type === 'credits' && (
                <Input label={`Valor ($) ${settings?.max_credits_amount ? `(máx: $${settings.max_credits_amount})` : ''}`} type="number" value={createForm.value} onChange={(v) => setCreateForm(p => ({ ...p, value: parseFloat(v) || 0 }))} />
              )}
              {createForm.type === 'trial' && (
                <Input label={`Dias de Trial ${settings?.max_trial_days ? `(máx: ${settings.max_trial_days})` : ''}`} type="number" value={createForm.trial_days} onChange={(v) => setCreateForm(p => ({ ...p, trial_days: parseInt(v) || 0 }))} />
              )}
              {createForm.type === 'discount_percent' && (
                <Input label={`Desconto (%) ${settings?.max_discount_percent ? `(máx: ${settings.max_discount_percent}%)` : ''}`} type="number" value={createForm.value} onChange={(v) => setCreateForm(p => ({ ...p, value: parseInt(v) || 0 }))} />
              )}
              <Input label={`Máx. Resgates ${settings?.max_redemptions_per_campaign ? `(máx: ${settings.max_redemptions_per_campaign})` : ''}`} type="number" value={createForm.max_redemptions} onChange={(v) => setCreateForm(p => ({ ...p, max_redemptions: parseInt(v) || 0 }))} />
            </div>
          </Modal>
        </div>
      )}

      {/* ── Meus Ganhos ── */}
      {tab === 'earnings' && (
        <div>
          {earnings.length === 0 ? (
            <div style={{ ...s.card, textAlign: 'center', color: T.textMuted }}>Nenhum ganho registrado ainda</div>
          ) : (
            <>
              <table style={s.table}>
                <thead><tr>
                  <th style={s.th}>Cliente</th><th style={s.th}>Tipo</th><th style={s.th}>Valor Transação</th>
                  <th style={s.th}>Comissão %</th><th style={s.th}>Valor Comissão</th><th style={s.th}>Data</th>
                </tr></thead>
                <tbody>
                  {earnings.map((e: any) => (
                    <tr key={e.id}>
                      <td style={s.td}>{e.customer_email}</td>
                      <td style={s.td}><UIBadge variant={e.source === 'llm_credits' ? 'info' : 'neutral'} color={e.source === 'llm_credits' ? undefined : T.purple}>{e.source === 'llm_credits' ? 'LLM' : 'Sub'}</UIBadge></td>
                      <td style={s.td}>${e.transaction_amount?.toFixed(2)}</td>
                      <td style={s.td}>{e.commission_percent}%</td>
                      <td style={s.td}><b style={{ color: T.success }}>${e.commission_amount?.toFixed(2)}</b></td>
                      <td style={s.td}>{fmt(e.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ marginTop: 16, textAlign: 'right', fontSize: 14, color: T.text }}>
                <b>Total: </b>
                <span style={{ color: T.success, fontWeight: 700 }}>
                  ${earnings.reduce((acc: number, e: any) => acc + (e.commission_amount || 0), 0).toFixed(2)}
                </span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
